import * as _ from 'lodash-es';
import { escapeText } from 'quill/blots/text';
import type { Bounds, EmitterSource, Op, Range } from 'quill/core';
import { Delta, Parchment } from 'quill/core';
import React, { createRef } from 'react';
import Quill from '../QuillEditor';
import { getRangeSaver } from '../RangeSaver';
import Code, { getPreviewRenderer } from '../code/CodeEmbed';
import { PlainLanguage, identifyLanguage } from '../code/Prism';
import type { BlockEmbedMovedInOptions } from '../types';
import getRangeFromPoint from '../utils/getRangeFromPoint';
import ReactBlockEmbed from './ReactBlockEmbed';
import CodeEmbedToken from './namespaces/code/CodeEmbedToken';
import translateBoundary from './utils/translateBoundary';

// eslint-disable-next-line import/no-unused-modules -- exported for testing
export const registerCodeEmbed = () => {
  Delta.registerEmbed('code-embed', {
    compose(a: Op[], b: Op[]) {
      return new Delta(a).compose(new Delta(b)).ops;
    },
    transform(a: Op[], b: Op[], priority: boolean) {
      return new Delta(a).transform(new Delta(b), priority).ops;
    },
    invert(a: Op[], b: Op[]) {
      return new Delta(a).invert(new Delta(b)).ops;
    },
  });
};

registerCodeEmbed();

const filterTokens = (change: Op[]): Delta => {
  const ops = change.map((op) => {
    if (!op.attributes?.[CodeEmbedToken.keyName]) return op;
    const newAttributes = _.omit(op.attributes, [CodeEmbedToken.keyName]);
    return Object.keys(newAttributes).length
      ? {
          ...op,
          attributes: newAttributes,
        }
      : _.omit(op, ['attributes']);
  });
  const delta = new Delta();
  ops.forEach((op) => delta.push(op));
  return delta;
};

type State = {
  value: Op[];
  language: string | null;
  isPreviewing: boolean;
  syntaxError: Error | null;
  blotCommentIds: string[];
};

class CodeEmbed extends ReactBlockEmbed {
  static blotName = 'code-embed';
  static className = 'ql-code-embed';
  static tagName = 'div';

  static ALLOWED_ATTRIBUTES = ['language'];

  static setValue() {}

  static value() {
    return true;
  }

  static formats(domNode: HTMLElement) {
    const attribute = 'data-language';
    let rawLanguage = domNode.getAttribute(attribute);
    if (!rawLanguage) {
      const codeElement = domNode.querySelector('code');
      if (codeElement) {
        codeElement.classList.forEach((className) => {
          if (className.startsWith('language-')) {
            rawLanguage = className.replace('language-', '');
          }
        });
      }
    }

    const language = rawLanguage && identifyLanguage(rawLanguage);
    return language ? { language } : {};
  }

  quillRef = createRef<Quill | null>();
  protected readonly disablePreviewing: boolean = false;
  protected readonly disableToolbar: boolean = false;
  allowPreviewingAtFirstRender = true;

  constructor(scroll: Parchment.ScrollBlot, domNode: Node, value?: Op[]) {
    super(scroll, domNode, value);

    this.setState({
      value,
      language: null,
      isPreviewing: false,
      syntaxError: null,
      blotCommentIds: [],
    });
  }

  formats() {
    return {
      ...this.attributes.values(),
      ...this.statics.formats(this.domNode, this.scroll),
    };
  }

  format(name: string, value: unknown) {
    if (name === 'language') {
      if (typeof value === 'string') {
        this.domNode.setAttribute(`data-${name}`, value);
        this.setState({ [name]: value });
      } else {
        this.domNode.removeAttribute(`data-${name}`);
        this.setState({ [name]: null });
      }
      this.validateSyntax();
      this.validateSyntax.flush();
    } else if (name === 'comment' || name === 'span') {
      const attributor = this.scroll.query(name, Parchment.Scope.ATTRIBUTE);
      if (attributor) {
        // @ts-expect-error
        this.attributes.attribute(attributor, value);
        this.setState({ blotCommentIds: value });
      }
    }
  }

  updateContent(change: Op[]) {
    const doc = this.value()['code-embed'];
    const delta = filterTokens(change);
    const newValue = new Delta(doc).compose(delta);

    this.quillRef.current?.updateContents(delta, Quill.sources.API);

    this.setState({ value: newValue.ops });
    this.validateSyntax();
  }

  value(): { 'code-embed': Op[] } {
    // @ts-expect-error
    return { [this.statics.blotName]: this.store.getState().value };
  }

  handleUserChange = (change: Delta) => {
    const filteredChange = filterTokens(change.ops);
    const newValue = new Delta(this.value()['code-embed']).compose(
      filteredChange,
    ).ops;
    this.setState({ value: newValue });
    this.validateSyntax();

    // @ts-expect-error
    this.scroll.emitEmbedUpdate(this, filteredChange.ops);
  };

  validateSyntax = _.debounce(() => {
    const { language, syntaxError } = this.store.getState() as State;
    const render = language ? getPreviewRenderer(language) : null;
    if (render) {
      const code = this.code();
      render.renderToHTML(code).then(
        () => this.setState({ syntaxError: null }),
        (error) => this.setState({ syntaxError: error }),
      );
    } else if (syntaxError) {
      this.setState({ syntaxError: null });
    }
  }, 500);

  activateSelection() {
    this.quillRef.current?.activateSelection();
  }

  setSelection(index: number, length: number, source: EmitterSource) {
    this.quillRef.current?.setSelection(index, length, source);
  }

  getLastRange() {
    if (!this.quillRef.current) return null;
    return getRangeSaver(this.quillRef.current).getLast() || null;
  }

  getQuill() {
    return this.quillRef.current;
  }

  getClientRects(range: Range) {
    const quill = this.quillRef.current;
    if (!quill) return [];
    return quill.getClientRects(range.index, range.length).map((rect) =>
      translateBoundary(
        rect,
        quill.container,
        // @ts-expect-error
        this.scroll.domNode.parentElement,
      ),
    );
  }

  getBounds(code: Range): Bounds | null {
    const quill = this.quillRef.current;
    if (quill) {
      const bounds = quill.getBounds(code);
      if (bounds && this.scroll.domNode.parentElement) {
        return translateBoundary(
          bounds,
          quill.container,
          this.scroll.domNode.parentElement,
        );
      }
    }
    return null;
  }

  onCaretMovedInWithKeyboard(movedInOptions: BlockEmbedMovedInOptions) {
    const quill = this.quillRef.current;
    if (!quill) return;

    if (movedInOptions.direction === 'left') {
      quill.setSelection(quill.getLength() - 1, 0, Quill.sources.USER);
      return;
    }
    if (movedInOptions.direction === 'right') {
      quill.setSelection(0, 0, Quill.sources.USER);
      return;
    }

    // Copy the native behavior that ArrowUp/ArrowDown should move
    // the caret to the same x position of the prev/next line.
    const node =
      movedInOptions.direction === 'up'
        ? quill.root.lastElementChild
        : quill.root.firstElementChild;
    if (!node) return;

    const bounds = node.getBoundingClientRect();
    const verticalPosition = (bounds.top + bounds.bottom) / 2;
    const range = getRangeFromPoint(movedInOptions.xOffset, verticalPosition);

    if (range && quill.root.contains(range.commonAncestorContainer)) {
      const sel = document.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    } else {
      const blot = quill.scroll.find(node);
      if (blot) {
        quill.setSelection(blot.offset(quill.scroll), 0, Quill.sources.USER);
      }
    }
  }

  code(range?: Range) {
    const code = this.value()
      ['code-embed'].map((op) =>
        typeof op.insert === 'string' ? op.insert : '',
      )
      .join('');
    return range ? code.slice(range.index, range.index + range.length) : code;
  }

  html(range?: Range) {
    const { language } = this.store.getState() as { language: string | null };
    const languageAttribute =
      language && language !== PlainLanguage.key
        ? ` class="language-${language}"`
        : '';

    return `<pre><code${languageAttribute}>${escapeText(
      this.code(range),
    )}</code></pre>`;
  }

  get isPreviewing() {
    // @ts-expect-error
    return this.store.getState().isPreviewing as boolean;
  }

  setIsPreviewing = (isPreviewing: boolean) => {
    this.setState({ isPreviewing });
  };

  render(state: State) {
    const { value, language, syntaxError, blotCommentIds } = state;

    return (
      <Code
        blot={this}
        language={language}
        syntaxError={syntaxError}
        content={value}
        blotCommentIds={blotCommentIds}
        isPreviewing={this.isPreviewing}
        setIsPreviewing={this.setIsPreviewing}
        disablePreviewing={this.disablePreviewing}
        disableToolbar={this.disableToolbar}
        quillRef={this.quillRef}
        onContentChange={this.handleUserChange}
        allowPreviewingAtFirstRender={this.allowPreviewingAtFirstRender}
      />
    );
  }
}

export default CodeEmbed;
